import { Component, EventEmitter, Input, Output, ChangeDetectionStrategy } from '@angular/core'
import { isImportedFromABBV2 } from '@tokeet-frontend/inquiry'
import { Inquiry, isInhouseEligible } from '@tv3/store/inquiry/inquiry.model'
import * as R from 'ramda'
import { Rental, Toaster } from '@tokeet-frontend/tv3-platform'
import { InquiriesListService } from '@tv3/containers/inquiries/shared/inquiries-list.service'
import { InquiryDialogsService } from '@tv3/containers/inquiries/dialogs/inquiry-dialogs.service'
import { DownloadInquiriesDialogService } from '../../dialogs/scoped/download/download-inquiries-dialog.service'
import { AmplitudeService } from '@tv3/services/amplitude.service'

@Component({
  selector: 'app-inquiries-action-btn',
  templateUrl: './inquiries-action-btn.component.html',
  styleUrls: ['./inquiries-action-btn.component.scss'],
  changeDetection: ChangeDetectionStrategy.Eager,
  standalone: false,
})
export class InquiriesActionBtnComponent {
  @Input() inquiries: Inquiry[] = []
  @Input() rentals: Rental[] = []
  @Input() filters

  items: Inquiry[] = []

  @Input() btnLabel = $localize`Action`
  @Input() btnClass = 'btn btn-secondary-warning'
  @Input() dropdownRight = true

  @Input('selection')
  set selectedInquiries(inquiries: Inquiry[]) {
    this.items = inquiries || []
    this.isAnyUnRead = R.any((i: Inquiry) => !i.read, inquiries) || R.isEmpty(inquiries)
    this.isAnyUnArchived = R.any((i: Inquiry) => !i.archived, inquiries) || R.isEmpty(inquiries)
    this.isAnyNoFollow = R.any((i: Inquiry) => !i.followup, inquiries) || R.isEmpty(inquiries)
    this.isAnyUnpaid = R.any((i: Inquiry) => !i.paid, inquiries) || R.isEmpty(inquiries)
    this.isAnyNotInhouse = R.any((i: Inquiry) => !i.inhouse && isInhouseEligible(i), inquiries) || R.isEmpty(inquiries)
    this.isAnyInhouse = R.any((i: Inquiry) => !!i.inhouse, inquiries)
    this.isAnyImportedFromABBV2 = R.any((i: Inquiry) => isImportedFromABBV2(i), inquiries)
  }
  @Output() deselect = new EventEmitter()

  isAnyUnRead = false
  isAnyUnArchived = false
  isAnyNoFollow = false
  isAnyUnpaid = false
  isAnyNotInhouse = false
  isAnyInhouse = false
  isAnyImportedFromABBV2 = false

  constructor(
    private toaster: Toaster,
    private dialogsService: InquiryDialogsService,
    private inquiriesListService: InquiriesListService,
    private downloadInquiriesDialog: DownloadInquiriesDialogService,
    private amplitudeService: AmplitudeService
  ) {}

  getSelected(field?: string) {
    if (field) {
      return R.map((i: Inquiry) => i[field], this.items)
    } else {
      return this.items
    }
  }

  onLink() {
    this.amplitudeService.logEvent('inquiries-action-link')
    const ids = this.getSelected('id')
    this.inquiriesListService.link(ids).subscribe(() => {
      this.deselect.next()
    })
  }

  onImport() {
    this.amplitudeService.logEvent('inquiries-action-import-airbnb')
    const selected = this.getSelected()
    this.inquiriesListService.importFromAirbnb(selected).subscribe(() => {
      this.deselect.next()
    })
  }

  onArchive() {
    this.amplitudeService.logEvent('inquiries-action-archive')
    const selected = this.getSelected()
    this.inquiriesListService.archive(selected).subscribe(() => {
      this.deselect.next()
    })
  }

  onUnArchive() {
    this.amplitudeService.logEvent('inquiries-action-unarchive')
    const selected = this.getSelected()
    this.inquiriesListService.unArchive(selected).subscribe(() => {
      this.deselect.next()
    })
  }

  onFollowUp() {
    this.amplitudeService.logEvent('inquiries-action-followup')
    const selected = this.getSelected()
    this.inquiriesListService.followUp(selected).subscribe(() => {
      this.deselect.next()
    })
  }

  onNoFollowUp() {
    this.amplitudeService.logEvent('inquiries-action-no-followup')
    const selected = this.getSelected()
    this.inquiriesListService.noFollowUp(selected).subscribe(() => {
      this.deselect.next()
    })
  }

  onRead() {
    this.amplitudeService.logEvent('inquiries-action-read')
    const selected = this.getSelected()
    this.inquiriesListService.read(selected).subscribe(() => {
      this.deselect.next()
    })
  }

  onUnRead() {
    this.amplitudeService.logEvent('inquiries-action-unread')
    const selected = this.getSelected()
    this.inquiriesListService.unRead(selected).subscribe(() => {
      this.deselect.next()
    })
  }

  onPaid() {
    this.amplitudeService.logEvent('inquiries-action-paid')
    const selected = this.getSelected()
    this.inquiriesListService.paid(selected).subscribe(() => {
      this.deselect.next()
    })
  }

  onUnpaid() {
    this.amplitudeService.logEvent('inquiries-action-unpaid')
    const selected = this.getSelected()
    this.inquiriesListService.unpaid(selected).subscribe(() => {
      this.deselect.next()
    })
  }

  onInhouse() {
    const selected = this.getSelected()
    this.inquiriesListService.inhouse(selected).subscribe(() => {
      this.deselect.next()
    })
  }

  onNoInhouse() {
    const selected = this.getSelected()
    this.inquiriesListService.noInhouse(selected).subscribe(() => {
      this.deselect.next()
    })
  }

  onAddTags() {
    this.amplitudeService.logEvent('inquiries-action-add-tags')
    const selected = this.getSelected()
    if (selected.length < 1) {
      this.toaster.warning($localize`Please select at least one inquiry.`)
      return
    }
    this.dialogsService.openAddTags(selected)
  }

  onDelete() {
    this.amplitudeService.logEvent('inquiries-action-delete')
    const selected = this.getSelected()
    this.inquiriesListService.delete(selected).subscribe()
  }

  getFilteredData() {
    const selected = this.getSelected()
    return selected.length ? selected : this.inquiries
  }

  onDownload() {
    this.amplitudeService.logEvent('inquiries-action-download')
    const filteredData = this.getFilteredData()
    this.downloadInquiriesDialog.open(filteredData, this.filters, this.rentals)
  }

  onExportReport() {
    this.amplitudeService.logEvent('inquiries-action-export-report')
    this.dialogsService.openExport()
  }

  onImportCSV() {
    this.amplitudeService.logEvent('inquiries-action-import-csv')
    this.dialogsService.openImport()
  }
}
